/** TanStack Query hooks for job management (SPA_TANSTACK_QUERY). */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, type PaginatedResponse } from "@/services/api";
import type { Job } from "@/types";

export function useSubmitJob() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useMutation<{ jobId: string }, Error, { pluginId: string; parameters: Record<string, unknown>; jobType?: string; name?: string }>({
    mutationFn: (body) => api.post("/reporting/jobs", body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      navigate(`/jobs/${data.jobId}`);
    },
  });
}

export function useJob(jobId: string) {
  return useQuery<Job>({
    queryKey: ["job", jobId],
    queryFn: () => api.get(`/reporting/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "complete" || status === "failed" || status === "completed_with_errors") return false;
      return 3000;
    },
  });
}

export function useJobs(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters);
  return useInfiniteQuery<PaginatedResponse<Job>>({
    queryKey: ["jobs", filters],
    queryFn: ({ pageParam }) => {
      if (pageParam) params.set("nextToken", pageParam as string);
      const qs = params.toString();
      const path = qs ? `/reporting/jobs?${qs}` : "/reporting/jobs";
      return api.get(path);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
  });
}

export function useJobResult(jobId: string, enabled = true) {
  return useQuery<{ content: string | Record<string, unknown>; format: string; downloadOnly?: boolean }>({
    queryKey: ["jobResult", jobId],
    queryFn: () => api.get(`/reporting/jobs/${jobId}/result`),
    enabled: !!jobId && enabled,
  });
}

export function useDownloadUrl(jobId: string) {
  return useQuery<{ url: string }>({
    queryKey: ["jobDownload", jobId],
    queryFn: () => api.get(`/reporting/jobs/${jobId}/download`),
    enabled: false, // Manual trigger via refetch
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (jobId) => api.del(`/reporting/jobs/${jobId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs"] }),
  });
}
